import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { mergePM } from "@/lib/partnershipContent";
import { computeComparison, SCENARIOS } from "@/lib/partnershipEngine";
import { Button } from "@/components/ui/button";
import { Handshake, ExternalLink, Loader2, RotateCcw, Plus, Trash2, Copy, CheckCircle2, AlertTriangle } from "lucide-react";
import { Field, Sel, Panel, SettingsForm } from "@/components/admin/PartnershipSettingsForm";

const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(Math.round(n || 0))}`;
const MODELS = [["fixed", "Fixed Rental"], ["management", "Management"], ["mg", "MG + Rev Share"]];

const linesToArr = (t) => t.split("\n").map((x) => x.trim()).filter(Boolean);

// ── Content form (global only) ──
function ContentForm({ cfg, set }) {
  const setC = (k, v) => set("content", { ...cfg.content, [k]: v });
  const setModel = (m, k, v) => set("models", { ...cfg.models, [m]: { ...cfg.models[m], [k]: v } });
  return (
    <>
      <Panel title="Owner-facing copy">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Heading" value={cfg.content.heading} onChange={(v) => setC("heading", v)} testId="pm-cms-heading" />
          <Field label="Suggested badge label" value={cfg.content.suggested_label} onChange={(v) => setC("suggested_label", v)} />
          <Field label="Intro" textarea value={cfg.content.intro} onChange={(v) => setC("intro", v)} className="sm:col-span-2" />
          <Field label="Preference question" value={cfg.content.preference_question} onChange={(v) => setC("preference_question", v)} />
          <div />
          <Field label="Option — guaranteed" value={cfg.content.preference_options.guaranteed} onChange={(v) => setC("preference_options", { ...cfg.content.preference_options, guaranteed: v })} />
          <Field label="Option — maximum" value={cfg.content.preference_options.maximum} onChange={(v) => setC("preference_options", { ...cfg.content.preference_options, maximum: v })} />
          <Field label="Option — balanced" value={cfg.content.preference_options.balanced} onChange={(v) => setC("preference_options", { ...cfg.content.preference_options, balanced: v })} />
          <Field label="Upside phrase" value={cfg.content.upside_phrase} onChange={(v) => setC("upside_phrase", v)} />
          <Field label="Protection phrase" value={cfg.content.protection_phrase} onChange={(v) => setC("protection_phrase", v)} />
          <Field label="Disclaimer" textarea rows={3} value={cfg.content.disclaimer} onChange={(v) => setC("disclaimer", v)} className="sm:col-span-2" testId="pm-cms-disclaimer" />
          <Field label="CTA heading" value={cfg.content.cta_heading} onChange={(v) => setC("cta_heading", v)} />
          <Field label="CTA text" value={cfg.content.cta_text} onChange={(v) => setC("cta_text", v)} />
          <Field label="CTA — assessment button" value={cfg.content.cta_assessment} onChange={(v) => setC("cta_assessment", v)} />
          <Field label="CTA — download button" value={cfg.content.cta_download} onChange={(v) => setC("cta_download", v)} />
          <Field label="CTA — speak button" value={cfg.content.cta_speak} onChange={(v) => setC("cta_speak", v)} />
        </div>
      </Panel>
      {MODELS.map(([m, ml]) => (
        <Panel key={m} title={`Model content — ${ml}`}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" value={cfg.models[m].name} onChange={(v) => setModel(m, "name", v)} testId={`pm-cms-model-name-${m}`} />
            <Field label="Badge" value={cfg.models[m].badge} onChange={(v) => setModel(m, "badge", v)} />
            <Field label="Headline" value={cfg.models[m].headline} onChange={(v) => setModel(m, "headline", v)} className="sm:col-span-2" />
            <Field label="Description" textarea value={cfg.models[m].desc} onChange={(v) => setModel(m, "desc", v)} className="sm:col-span-2" />
            <Field label="Risk label" value={cfg.models[m].risk_label} onChange={(v) => setModel(m, "risk_label", v)} />
            <Field label="Income type" value={cfg.models[m].income_type} onChange={(v) => setModel(m, "income_type", v)} />
            <Field label="Expenses paid by" value={cfg.models[m].expenses_paid_by} onChange={(v) => setModel(m, "expenses_paid_by", v)} />
            <Field label="Key benefit" value={cfg.models[m].key_benefit} onChange={(v) => setModel(m, "key_benefit", v)} />
            <Field label="Main trade-off" value={cfg.models[m].trade_off} onChange={(v) => setModel(m, "trade_off", v)} />
            {m === "management" && <Field label="Owner-facing explanation" textarea value={cfg.models[m].explain || ""} onChange={(v) => setModel(m, "explain", v)} />}
            <Field label={`${cfg.models[m].gains_title} (one per line)`} textarea rows={4} value={(cfg.models[m].gains || []).join("\n")} onChange={(v) => setModel(m, "gains", linesToArr(v))} />
            <Field label={`${cfg.models[m].gives_title} (one per line)`} textarea rows={4} value={(cfg.models[m].gives || []).join("\n")} onChange={(v) => setModel(m, "gives", linesToArr(v))} />
            <Field label="Owner responsibilities (one per line)" textarea rows={4} value={(cfg.models[m].owner_responsibility || []).join("\n")} onChange={(v) => setModel(m, "owner_responsibility", linesToArr(v))} />
            <Field label="Happy Retreats responsibilities (one per line)" textarea rows={4} value={(cfg.models[m].hr_responsibility || []).join("\n")} onChange={(v) => setModel(m, "hr_responsibility", linesToArr(v))} />
          </div>
        </Panel>
      ))}
      <Panel title="Comparison table rows" right={
        <Button variant="outline" size="sm" onClick={() => set("comparison_rows", [...cfg.comparison_rows, { label: "", fixed: "", management: "", mg: "" }])}><Plus className="w-4 h-4 mr-1" /> Add row</Button>
      }>
        {cfg.comparison_rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr,1fr,1fr,1fr,36px] gap-2 mb-2 items-end">
            <Field label={i === 0 ? "Parameter" : ""} value={r.label} onChange={(v) => set("comparison_rows", cfg.comparison_rows.map((x, j) => (j === i ? { ...x, label: v } : x)))} />
            <Field label={i === 0 ? "Fixed rental" : ""} value={r.fixed} onChange={(v) => set("comparison_rows", cfg.comparison_rows.map((x, j) => (j === i ? { ...x, fixed: v } : x)))} />
            <Field label={i === 0 ? "Management" : ""} value={r.management} onChange={(v) => set("comparison_rows", cfg.comparison_rows.map((x, j) => (j === i ? { ...x, management: v } : x)))} />
            <Field label={i === 0 ? "MG + share" : ""} value={r.mg} onChange={(v) => set("comparison_rows", cfg.comparison_rows.map((x, j) => (j === i ? { ...x, mg: v } : x)))} />
            <Button variant="outline" size="icon" onClick={() => set("comparison_rows", cfg.comparison_rows.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </Panel>
    </>
  );
}

// ── Formulas & logic documentation ──
function Formula({ children }) {
  return <div className="font-mono text-[11.5px] bg-zinc-900 text-emerald-300 rounded-md px-3 py-2 my-1.5 overflow-x-auto whitespace-pre">{children}</div>;
}
function Doc({ title, children }) {
  return (
    <div className="border rounded-md p-4 mb-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-zinc-600 mb-2">{title}</div>
      <div className="text-[13px] text-zinc-600 leading-relaxed space-y-1.5">{children}</div>
    </div>
  );
}
function Cur({ children }) {
  return <span className="inline-block bg-amber-50 border border-amber-200 text-amber-800 rounded px-1.5 py-0.5 text-[11px] font-medium mx-0.5">{children}</span>;
}

function LogicDocs({ cfg }) {
  const ex = useMemo(() => computeComparison(10000000, cfg), [cfg]);
  const varTotal = (cfg.expenses || []).filter((e) => e.active !== false && e.type === "variable").reduce((a, e) => a + Number(e.pct || 0), 0);
  const scen = cfg.scenarios;
  return (
    <div data-testid="pm-cms-docs">
      <Panel title="How the Compare Partnership Models module calculates everything">
        <p className="text-[13px] text-zinc-500 mb-4">
          This is a living reference: the <Cur>amber chips</Cur> always show your <strong>currently saved/edited values</strong>, and the worked example at the bottom recomputes live from them.
          Change anything under <em>Settings &amp; formulas</em> and this page updates instantly. <strong>R</strong> = projected annual revenue excluding taxes, coming from the Revenue Calculator (or typed on the standalone page).
        </p>

        <Doc title="1 · Revenue scenarios">
          <p>Every figure is computed under three revenue scenarios, derived from R using your multipliers:</p>
          <Formula>{`Conservative revenue      = R × ${scen.conservative.multiplier}%
Projected revenue         = R × ${scen.projected.multiplier}%
High-performance revenue  = R × ${scen.high.multiplier}%`}</Formula>
          <p>Current multipliers: <Cur>{scen.conservative.label} {scen.conservative.multiplier}%</Cur> <Cur>{scen.projected.label} {scen.projected.multiplier}%</Cur> <Cur>{scen.high.label} {scen.high.multiplier}%</Cur></p>
        </Doc>

        <Doc title="2 · Operating expense engine">
          <p>Each category is computed <strong>per scenario</strong> according to its type, then summed. Only categories switched ON count.</p>
          <Formula>{`Variable  →  Scenario revenue × category %
Fixed     →  Annual amount   (or Monthly × 12, or % of base projected revenue R)
Hybrid    →  Fixed portion + Scenario revenue × variable %`}</Formula>
          <p>
            Active categories: <Cur>{(cfg.expenses || []).filter((e) => e.active !== false).length}</Cur> ·
            Variable total: <Cur>{varTotal}% of revenue</Cur> ·
            Total opex at projected revenue: <Cur>{inr(ex.opex.projected)} ({ex.opexPctTotal.toFixed(1)}% of R)</Cur>
          </p>
          <p>The <em>Paid by</em> selector per category decides whose pocket it comes from in each model — it directly changes owner income. Defaults: Happy Retreats pays in Fixed Rental and MG+Share; the Owner pays in the Management Model.</p>
        </Doc>

        <Doc title="3 · Happy Retreats central support cost">
          <p>A separate cost (never mixed into property opex) covering central sales, marketing systems, revenue management, technology, brand, finance, recruitment, training and leadership:</p>
          <Formula>{`Central support = Scenario revenue × ${cfg.central_support_pct}%`}</Formula>
          <p>Currently <Cur>{cfg.central_support_pct}%</Cur> → {inr(ex.central.projected)} at projected revenue.</p>
        </Doc>

        <Doc title="4 · Target operator profit & commercial viability (internal only)">
          <Formula>{`Operator margin = Operator profit ÷ Scenario revenue × 100

Healthy          margin ≥ ${cfg.viability.healthy_pct}%
Review Required  ${cfg.viability.review_pct}% ≤ margin < ${cfg.viability.healthy_pct}%
Not Sustainable  margin < ${cfg.viability.review_pct}%`}</Formula>
          <p>Minimum target operator profit <Cur>{cfg.viability.min_profit_pct}% of revenue</Cur> is used in the sustainable-rent ceiling and the max-sustainable-payout check. Opex warning threshold: <Cur>{cfg.viability.opex_warn_pct}%</Cur>. Owners never see this — it lives in the <em>Internal profitability</em> tab.</p>
        </Doc>

        <Doc title="5 · Model 1 — Fixed Rental">
          <p>Active method: <Cur>{cfg.fixed_rental.method === "manual" ? "B — Manual amount" : cfg.fixed_rental.method === "sustainable" ? "C — Sustainable formula" : "A — % of projected revenue"}</Cur></p>
          <Formula>{`A · Percentage    Rent = R × ${cfg.fixed_rental.rent_pct}%
B · Manual        Rent = manual annual (or monthly × 12)
C · Sustainable   Ceiling = Conservative revenue − Conservative opex
                            − Conservative central support − Target operator profit
                  Rent    = LOWER of (A) and Ceiling

Owner annual earnings  = Annual fixed rent   (identical in all scenarios)
Owner monthly earnings = Annual fixed rent ÷ 12`}</Formula>
          <p>Manual override (<Cur>{cfg.fixed_rental.override_annual > 0 ? inr(cfg.fixed_rental.override_annual) : "off"}</Cur>) beats every method when set. With current settings, rent = <Cur>{inr(ex.rent)}</Cur> and the sustainable ceiling = <Cur>{inr(ex.sustainableCeiling)}</Cur>.</p>
          <p><strong>Property stage override:</strong> when the owner selects a stage, the Year-1 % from that stage's offer schedule replaces the rent % above (and the revenue-share % in Model 3). Later years show as an escalation timeline. Edit schedules under <em>Property stage bands</em>.</p>
          <p><strong>Opportunity cost shown to the owner</strong> (never worded as guaranteed lost income):</p>
          <Formula>{`Upside vs management     = Management owner net (projected) − Rent = ${inr(Math.max(0, ex.oppCost.mgmt_projected))}
Upside vs revenue share  = MG+Share payout (projected) − Rent      = ${inr(Math.max(0, ex.oppCost.mg_projected))}`}</Formula>
        </Doc>

        <Doc title="6 · Model 2 — Management Model">
          <Formula>{`Fee            = Scenario revenue × ${cfg.management.fee_pct}%${cfg.management.slabs_enabled ? "   (slab % by revenue overrides this)" : ""}
${cfg.management.min_fee_enabled ? `With min fee   Fee = HIGHER of (percentage fee, ${inr(Math.max(cfg.management.min_annual, cfg.management.min_monthly * 12))})\n` : ""}${cfg.management.incentive_enabled ? `Incentive      Fee += (revenue − ${inr(cfg.management.incentive_threshold)}) × ${cfg.management.incentive_pct}%  when above threshold\n` : ""}Owner net      = Scenario revenue − Property opex (owner-paid) − Fee`}</Formula>
          <p>
            Fee %: <Cur>{cfg.management.fee_pct}%</Cur> ·
            Minimum fee: <Cur>{cfg.management.min_fee_enabled ? "on" : "off"}</Cur> ·
            Slabs: <Cur>{cfg.management.slabs_enabled ? `on (${(cfg.management.slabs || []).length})` : "off"}</Cur> ·
            Incentive: <Cur>{cfg.management.incentive_enabled ? "on" : "off"}</Cur>
          </p>
          <p>At projected revenue: fee {inr(ex.mgmtFee.projected)}, owner net <Cur>{inr(ex.ownerIncome.management.projected)}</Cur>. The owner funds the full {inr(ex.opex.projected)} operating expense and working capital — this is why the model carries the highest risk and the highest upside.</p>
        </Doc>

        <Doc title="7 · Model 3 — Minimum Guarantee with Revenue Share">
          <p>Payout method: <Cur>{cfg.mg_share.payout_method === "base_plus_upside" ? "Base guarantee + upside above threshold" : cfg.mg_share.payout_method === "manual" ? "Manual custom payout" : "Higher of MG or revenue share (default)"}</Cur></p>
          <Formula>{`Minimum guarantee = ${cfg.mg_share.mg_basis === "projected" ? "Projected" : "Conservative"} revenue × ${cfg.mg_share.mg_pct}%   (or manual MG when set)
Revenue share     = Scenario revenue × ${cfg.mg_share.share_pct}%

Higher-of method  Payout = MAX(Minimum guarantee, Revenue share)
Base + upside     Payout = MG + (revenue − ${inr(cfg.mg_share.upside_threshold)}) × ${cfg.mg_share.upside_pct}%  when above threshold`}</Formula>
          <p>Currently: MG = <Cur>{inr(ex.minGuarantee)}</Cur>, share at projected = {inr(ex.mgShareAmt.projected)}, owner receives <Cur>{inr(ex.mgPayout.projected)}</Cur>. If revenue underperforms, the guarantee floor protects the owner; if it outperforms, the share lifts the payout.</p>
        </Doc>

        <Doc title="8 · Operator P&L per model (Internal profitability tab)">
          <Formula>{`Fixed Rental   Profit = Revenue − HR-paid opex − Central support − Rent
Management     Profit = Management fee − Central support
MG + Share     Profit = Revenue − HR-paid opex − Central support − Owner payout

Max sustainable owner payout = Conservative revenue − Conservative opex
                               − Central support − Target operator profit
                             = ${inr(ex.maxSustainablePayout)} (current)`}</Formula>
          <p>Break-even revenue solves Profit = 0 using your fixed vs variable cost split. Warnings fire automatically for: margin below minimum, unsustainable fixed rent, MG exceeding sustainable payout, negative management owner return, opex above the warning limit, and revenue-share review.</p>
        </Doc>

        <Doc title={`9 · Live worked example — R = ₹1,00,00,000 with your current settings`}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[640px] border rounded-md" data-testid="pm-cms-docs-example">
              <thead><tr className="bg-zinc-50 text-zinc-400 text-[10px] uppercase">
                <th className="text-left px-3 py-2">Owner income</th>
                {SCENARIOS.map((s) => <th key={s} className="text-right px-3 py-2">{scen[s].label} ({inr(ex.revs[s])})</th>)}
              </tr></thead>
              <tbody>
                {MODELS.map(([m, ml]) => (
                  <tr key={m} className="border-t">
                    <td className="px-3 py-2 font-medium">{ml}</td>
                    {SCENARIOS.map((s) => <td key={s} className="px-3 py-2 text-right tabular-nums">{inr(ex.ownerIncome[m][s])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2">Cross-checks at projected: opex {inr(ex.opex.projected)} · central support {inr(ex.central.projected)} · rent {inr(ex.rent)} · MG {inr(ex.minGuarantee)} · share {inr(ex.mgShareAmt.projected)} · fixed-rent opportunity cost vs management {inr(Math.max(0, ex.oppCost.mgmt_projected))}.</p>
        </Doc>

        <Doc title="10 · Where each thing is edited">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Settings &amp; formulas tab</strong> — every number above: multipliers, expense categories (type, %, amounts, paid-by, on/off, order), central support, viability thresholds, all three model settings and overrides.</li>
            <li><strong>Owner-facing content tab</strong> — headings, model names/badges/descriptions, risk labels, gains/trade-offs, responsibilities, comparison-table rows, disclaimer, CTA wording.</li>
            <li><strong>Property overrides tab</strong> — per-property commercial versions (snapshots of all settings). Historical versions are never overwritten; duplicate to revise, mark one active.</li>
            <li><strong>Internal profitability tab</strong> — Happy Retreats-only P&amp;L, viability and warnings for any test revenue and any assumption set.</li>
            <li>Owners see plain-language "How was this calculated?" explainers on the public page — raw formulas stay in this admin only.</li>
          </ul>
        </Doc>
      </Panel>
    </div>
  );
}

// ── Internal profitability panel ──
function InternalPanel({ cfg, configs }) {
  const [testR, setTestR] = useState(10000000);
  const [source, setSource] = useState("global");
  const activeCfg = useMemo(() => {
    if (source === "global") return cfg;
    const v = configs.find((x) => x.id === source);
    return v ? mergePM(v.config) : cfg;
  }, [source, cfg, configs]);
  const calc = useMemo(() => computeComparison(Number(testR) || 0, activeCfg), [testR, activeCfg]);
  const vColor = (v) => (v === "Healthy" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : v === "Review Required" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200");

  return (
    <>
      <Panel title="Internal profitability — Happy Retreats view (never shown to owners)">
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <div className="w-64"><Field label="Test projected revenue (₹/yr)" type="number" value={testR} onChange={setTestR} testId="pm-cms-test-revenue" /></div>
          <div className="w-72">
            <Sel label="Assumption set" value={source} onChange={setSource}
              options={[["global", "Global default settings"], ...configs.map((c) => [c.id, `${c.property_name || "—"} · ${c.version_label}${c.active ? " (active)" : ""}`])]} testId="pm-cms-internal-source" />
          </div>
        </div>
        {MODELS.map(([m, ml]) => (
          <div key={m} className="border rounded-md mb-3 overflow-x-auto">
            <div className="px-4 py-2.5 text-xs font-semibold bg-zinc-50 border-b">{ml}</div>
            <table className="w-full text-xs min-w-[820px]">
              <thead><tr className="text-zinc-400 text-[10px] uppercase">
                <th className="text-left px-4 py-2">Scenario</th><th className="text-right px-3 py-2">Revenue</th><th className="text-right px-3 py-2">Op. expenses</th><th className="text-right px-3 py-2">Owner payout</th><th className="text-right px-3 py-2">Central support</th><th className="text-right px-3 py-2">Operator profit</th><th className="text-right px-3 py-2">Margin</th><th className="text-left px-3 py-2">Viability</th>
              </tr></thead>
              <tbody>
                {SCENARIOS.map((s) => {
                  const o = calc.operator[m][s];
                  return (
                    <tr key={s} className="border-t">
                      <td className="px-4 py-2">{activeCfg.scenarios[s].label}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{inr(calc.revs[s])}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{inr(o.expenses)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{inr(o.ownerPayout)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{inr(o.central)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-medium ${o.profit < 0 ? "text-red-600" : ""}`} data-testid={`pm-cms-profit-${m}-${s}`}>{inr(o.profit)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{o.margin.toFixed(1)}%</td>
                      <td className="px-3 py-2"><span className={`inline-block border rounded px-2 py-0.5 text-[10px] ${vColor(o.viability)}`} data-testid={`pm-cms-viability-${m}-${s}`}>{o.viability}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
        <div className="grid sm:grid-cols-3 gap-3 text-xs mt-2">
          <div className="border rounded-md p-3"><div className="text-zinc-400 text-[10px] uppercase">Break-even revenue — fixed rental</div><div className="font-semibold mt-1">{calc.breakEven.fixed ? inr(calc.breakEven.fixed) : "—"}</div></div>
          <div className="border rounded-md p-3"><div className="text-zinc-400 text-[10px] uppercase">Break-even revenue — MG + share</div><div className="font-semibold mt-1">{calc.breakEven.mg ? inr(calc.breakEven.mg) : "—"}</div></div>
          <div className="border rounded-md p-3"><div className="text-zinc-400 text-[10px] uppercase">Max sustainable owner payout</div><div className="font-semibold mt-1" data-testid="pm-cms-max-payout">{inr(calc.maxSustainablePayout)}</div></div>
        </div>
        {calc.warnings.length > 0 && (
          <div className="mt-4 border border-amber-200 bg-amber-50 rounded-md p-4" data-testid="pm-cms-warnings">
            <div className="text-xs font-semibold text-amber-800 flex items-center gap-1.5 mb-2"><AlertTriangle className="w-4 h-4" /> Warnings</div>
            <ul className="text-xs text-amber-800 space-y-1">{calc.warnings.map((w, i) => <li key={i}>· {w}</li>)}</ul>
          </div>
        )}
      </Panel>
    </>
  );
}

// ── Property overrides ──
function Overrides({ globalCfg, configs, reload }) {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    const property_name = window.prompt("Property name for this commercial version:");
    if (property_name === null) return;
    try {
      const { data } = await api.post("/admin/partnership-configs", {
        property_name, version_label: `Version 1`, notes: "", effective_date: "", config: globalCfg,
      });
      await reload();
      setEditing(data);
      toast.success("Version created from current global settings");
    } catch { toast.error("Could not create version"); }
  };

  const saveEditing = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/partnership-configs/${editing.id}`, {
        property_name: editing.property_name, version_label: editing.version_label,
        notes: editing.notes || "", effective_date: editing.effective_date || "", config: editing.config,
      });
      await reload();
      toast.success("Version saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (editing) {
    const cfg = mergePM(editing.config);
    const set = (k, v) => setEditing((prev) => ({ ...prev, config: { ...mergePM(prev.config), [k]: v } }));
    return (
      <>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <Button variant="outline" onClick={() => setEditing(null)}>← Back to versions</Button>
          <Button onClick={saveEditing} disabled={saving} data-testid="pm-cms-version-save">{saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save version</Button>
        </div>
        <Panel title="Version details">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Property name" value={editing.property_name} onChange={(v) => setEditing((p) => ({ ...p, property_name: v }))} testId="pm-cms-version-property" />
            <Field label="Version label" value={editing.version_label} onChange={(v) => setEditing((p) => ({ ...p, version_label: v }))} testId="pm-cms-version-label" />
            <Field label="Effective date" type="date" value={editing.effective_date} onChange={(v) => setEditing((p) => ({ ...p, effective_date: v }))} />
            <Field label="Internal notes" textarea value={editing.notes} onChange={(v) => setEditing((p) => ({ ...p, notes: v }))} className="sm:col-span-3" testId="pm-cms-version-notes" />
          </div>
        </Panel>
        <SettingsForm cfg={cfg} set={set} />
      </>
    );
  }

  return (
    <Panel title="Property-level commercial versions" right={<Button onClick={create} data-testid="pm-cms-version-create"><Plus className="w-4 h-4 mr-1" /> New version from global</Button>}>
      <p className="text-xs text-zinc-500 mb-4">Each version is a full snapshot of assumptions for one property. Historical versions are never overwritten by global changes — duplicate to create a new revision, mark one active per property.</p>
      {configs.length === 0 && <div className="text-sm text-zinc-400 py-6 text-center">No property versions yet.</div>}
      {configs.map((cv) => (
        <div key={cv.id} className="border rounded-md px-4 py-3 mb-2 flex flex-wrap items-center gap-3" data-testid={`pm-cms-version-${cv.id}`}>
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-medium flex items-center gap-2">
              {cv.property_name || "—"} · {cv.version_label}
              {cv.active && <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"><CheckCircle2 className="w-3 h-3" /> Active</span>}
            </div>
            <div className="text-[11px] text-zinc-400">Updated {String(cv.updated_at || "").slice(0, 10)}{cv.effective_date ? ` · effective ${cv.effective_date}` : ""}{cv.notes ? ` · ${cv.notes.slice(0, 60)}` : ""}</div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setEditing(cv)} data-testid={`pm-cms-version-edit-${cv.id}`}>Edit</Button>
          <Button variant="outline" size="sm" onClick={async () => { await api.post(`/admin/partnership-configs/${cv.id}/duplicate`); await reload(); toast.success("Duplicated as new version"); }}><Copy className="w-3.5 h-3.5 mr-1" /> Duplicate</Button>
          {!cv.active && <Button variant="outline" size="sm" onClick={async () => { await api.post(`/admin/partnership-configs/${cv.id}/activate`); await reload(); toast.success("Marked active"); }} data-testid={`pm-cms-version-activate-${cv.id}`}>Mark active</Button>}
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={async () => { if (!window.confirm("Delete this version?")) return; await api.delete(`/admin/partnership-configs/${cv.id}`); await reload(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      ))}
    </Panel>
  );
}

export default function PartnershipCMS() {
  const [c, setC] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [tab, setTab] = useState("settings");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const reloadConfigs = async () => {
    try { const { data } = await api.get("/admin/partnership-configs"); setConfigs(data); } catch { /* noop */ }
  };

  useEffect(() => {
    api.get("/admin/content/partnership_models").then(({ data }) => setC(mergePM(data))).catch(() => setC(mergePM(null)));
    reloadConfigs();
  }, []);

  const set = (k, v) => { setDirty(true); setC((prev) => ({ ...prev, [k]: v })); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/partnership_models", c);
      setDirty(false);
      toast.success("Partnership model settings updated — live on the public pages");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const reset = () => {
    if (!window.confirm("Reset ALL partnership settings and content to defaults? You still need to Save.")) return;
    setC(mergePM(null));
    setDirty(true);
  };

  if (!c) return <div className="p-8 text-zinc-400">Loading…</div>;

  const TABS = [["settings", "Settings & formulas"], ["content", "Owner-facing content"], ["overrides", "Property overrides"], ["internal", "Internal profitability"], ["logic", "Formulas & logic"]];

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="partnership-cms">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Handshake className="h-6 w-6" /> Partnership Model Settings</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Every percentage, formula, label and threshold of the Compare Partnership Models module.{" "}
            <Link to="/partnership-models" target="_blank" className="inline-flex items-center gap-1 text-zinc-800 underline underline-offset-2">View public page <ExternalLink className="w-3 h-3" /></Link>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} data-testid="pm-cms-reset"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset to defaults</Button>
          <Button onClick={save} disabled={saving} data-testid="pm-cms-save">{saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save</Button>
        </div>
      </div>

      {dirty && <div className="mb-4 text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-md" data-testid="pm-cms-unsaved">Unsaved changes — click Save to publish.</div>}

      <div className="flex gap-1 mb-5 border-b">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-zinc-900 font-medium" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}
            data-testid={`pm-cms-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "settings" && <SettingsForm cfg={c} set={set} />}
      {tab === "content" && <ContentForm cfg={c} set={set} />}
      {tab === "overrides" && <Overrides globalCfg={c} configs={configs} reload={reloadConfigs} />}
      {tab === "internal" && <InternalPanel cfg={c} configs={configs} />}
      {tab === "logic" && <LogicDocs cfg={c} />}
    </div>
  );
}
